import { useMemo, useEffect, createContext } from 'react';

import { matchPath } from 'react-router-dom';

import { getExtendedLogger } from '@mindtickle/ui-shell-core/Logger';

import { isAdminSite } from '~/utils';
import { getStoredLocale, setStoredLocale } from '~/utils/userLocaleStorage';
import logger from '~/config/app.logger';

import { SHAREABLE_ROUTE_PREFIX_PATH } from '~/config/app.constants';

import { useDateTimeConfig, DEFAULT_LOCALE } from '../DateTranslation';
import type { UserLocaleDateConfig, StoredLocale } from '../DateTranslation';

import type { AuthLocaleFields, TLocalAuthContext } from './types';
import useUserAuth from './hooks/useUserAuth';
import { adminAuthMapper, learnerAuthMapper } from './helpers/mapper';

const log = getExtendedLogger('auth', logger);

const LocalAuthContext = createContext<TLocalAuthContext | undefined>(undefined);

const extractLocaleFields = (auth: AuthLocaleFields): StoredLocale | undefined => {
  const userId = auth?.id;
  const companyId = auth?.company?.id;
  const locale = auth?.locale || DEFAULT_LOCALE;
  if (userId && companyId && locale) return { userId, companyId, locale };
  return undefined;
};

const isSameStoredLocale = (a: StoredLocale, b: StoredLocale | undefined): boolean =>
  b?.userId === a.userId && b?.companyId === a.companyId && b?.locale === a.locale;

const syncUserLocale = (auth: AuthLocaleFields): void => {
  const fields = extractLocaleFields(auth);
  if (!fields) return;
  if (isSameStoredLocale(fields, getStoredLocale())) return;
  setStoredLocale(fields);
};

function LocalAuthProvider({ children }: { children?: React.ReactNode }) {
  const auth = useUserAuth();

  syncUserLocale(auth);

  const { setConfig: setDateTimeConfig } = useDateTimeConfig();

  let externalUserInteractionId: string | undefined;

  useEffect(() => {
    const locale: string = auth?.locale || DEFAULT_LOCALE;
    if (!locale) return;
    const localeConfig: UserLocaleDateConfig = auth?.tempFeatureConfig?.dateTranslation?.[locale];
    log('setDateTimeConfig — locale: %s, localeConfig: %o', locale, localeConfig);
    setDateTimeConfig({
      locale,
      is24HourFormat: localeConfig?.is24HourFormat,
      overrides: localeConfig,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.locale]);

  if (!auth.isLoggedIn) {
    const match = matchPath(SHAREABLE_ROUTE_PREFIX_PATH, window.location.pathname);
    externalUserInteractionId = match ? match.params.userInteractionId : undefined;
  }

  const value = useMemo(
    () =>
      isAdminSite()
        ? adminAuthMapper(auth, { externalUserInteractionId })
        : learnerAuthMapper(auth, { externalUserInteractionId }),
    [auth, externalUserInteractionId]
  );

  return <LocalAuthContext.Provider value={value}>{children}</LocalAuthContext.Provider>;
}

export { LocalAuthContext };
export default LocalAuthProvider;
